import { useEffect, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { ApiError } from "@workspace/api-client-react";
import { OBSERVABILITY_EVENTS } from "@workspace/observability";
import { track } from "@workspace/observability/web";
import { t } from "@workspace/strings/web";
import { useDevice } from "@/player/context/device-context";
import PlayerPage from "@/player/pages/player";
import { Button } from "@/components/ui/button";
import {
  readStoredCredential,
  setActiveScreenCredential,
} from "@/player/lib/screen-credential";
import { registerServiceWorker } from "@/player/lib/register-sw";
import { clearOperatorExited } from "@/lib/operator-exit";

registerServiceWorker();

function Gate({ onExit }: { onExit: () => void }) {
  const {
    credential,
    device,
    isLoading,
    error,
    release,
    adoptCredential,
    refetch,
  } = useDevice();
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);

  useEffect(() => {
    if (credential) return;
    const stored = readStoredCredential();
    if (stored) {
      adoptCredential(stored);
      return;
    }
    onExit();
  }, [credential, adoptCredential, onExit]);

  useEffect(() => {
    if (device || recoveryAttempted || isLoading) return;
    const stored = readStoredCredential();
    if (!stored) {
      setRecoveryAttempted(true);
      return;
    }
    setActiveScreenCredential(stored);
    refetch();
    setRecoveryAttempted(true);
  }, [device, isLoading, recoveryAttempted, refetch]);

  useEffect(() => {
    if (!device) return;
    track(OBSERVABILITY_EVENTS["player.started"], {
      device_id: device.id,
      workspace_id: device.workspaceId,
    });
  }, [device?.id, device?.workspaceId]);

  if (!credential) return null;

  if (!device && (isLoading || !recoveryAttempted)) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!device) {
    // CB-160: distinguish "the server rejected this screen" (an HTTP response —
    // the signed-out card) from "the server is unreachable" (network error —
    // never present offline as signed-out). A 401 already auto-released in the
    // provider; reaching here with an ApiError means e.g. a deleted device.
    // The reset is DESTRUCTIVE (wipes credential + offline cache), so it is
    // secondary and confirm-gated; a provisioned box with a cached window never
    // even lands here (the provider falls back to the cached DeviceInfo).
    const serverRejected = error instanceof ApiError;
    const confirmReset = () => {
      if (window.confirm(t("playerUi.unreachable.resetConfirm"))) release();
    };
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background px-6">
        <div className="max-w-sm text-center space-y-4">
          <AlertCircle className="size-10 text-destructive mx-auto" />
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              {serverRejected
                ? t("playerUi.signedOut.title")
                : t("playerUi.unreachable.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {serverRejected
                ? t("playerUi.signedOut.description")
                : t("playerUi.unreachable.description")}
            </p>
          </div>
          <div className="flex flex-col items-center gap-2">
            {!serverRejected && (
              <Button onClick={refetch} data-testid="button-device-retry">
                {t("playerUi.unreachable.retry")}
              </Button>
            )}
            <Button
              variant={serverRejected ? "default" : "ghost"}
              onClick={serverRejected ? release : confirmReset}
              data-testid="button-credential-reset"
            >
              {serverRejected
                ? t("playerUi.signedOut.action")
                : t("playerUi.unreachable.reset")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <PlayerPage />;
}

/**
 * Mounts the device runtime. DeviceProvider reads the persisted credential and
 * sets it as the active bearer token; when it is cleared we bubble back up to
 * the selection flow via onExit.
 */
export default function PlayerRuntime({ onExit }: { onExit: () => void }) {
  // Booting the runtime is a deliberate entry into the player surface — clear
  // any "operator signed out" flag so the kiosk route guard is re-armed.
  useEffect(() => {
    clearOperatorExited();
  }, []);

  return <Gate onExit={onExit} />;
}
